/**
 * spaceClient.js — minimal Gradio 5 Spaces client over plain fetch.
 *
 * Calls a Hugging Face Space's queue API without any dependency:
 *   POST {spaceUrl}/call/{fn}/           {"data": [...]}       -> {"event_id"}
 *   GET  {spaceUrl}/call/{fn}/{event_id}                       -> SSE stream
 *       events: process_starts, progress, complete (JSON payload)
 *
 * ZeroGPU cold-start 503s and queue fullbacks are retried with backoff.
 * Browser CORS: Spaces serve permissive CORS on /call/*, so direct browser
 * calls work with no proxy.
 */

const DEFAULT_TIMEOUT_MS = 300_000; // CPU-basic Space inference can take minutes
const MAX_ATTEMPTS = 3;

/**
 * Upload a file blob to the Space (multipart POST /upload).
 * Gradio's queue API cannot accept raw data URLs for gr.File inputs; files
 * must be uploaded first and referenced by server path, exactly as
 * gradio_client does internally.
 * @returns {Promise<string>} server-side temp path
 */
async function _uploadFile(spaceUrl, file, signal) {
  const form = new FormData();
  form.append('files', file, file.name || 'upload');
  const res = await fetch(`${spaceUrl.replace(/\/+$/, '')}/upload`, {
    method: 'POST',
    body: form,
    signal,
  });
  if (!res.ok) throw new Error(`upload failed (HTTP ${res.status})`);
  const paths = await res.json();
  if (!Array.isArray(paths) || !paths[0]) throw new Error('upload returned no path');
  return paths[0];
}

/** Wrap an uploaded server path as Gradio FileData. */
function _fileData(serverPath) {
  return { path: serverPath, meta: { _type: 'gradio.FileData' } };
}

/** POST the job, return event_id. Retries transient failures. */
async function _postJob(spaceUrl, fn, data, signal) {
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(`${spaceUrl.replace(/\/+$/, '')}/call/${fn}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
        signal,
      });
      if (res.status === 503 || res.status === 502 || res.status === 429) {
        throw new Error(`space unavailable (HTTP ${res.status}) — Space may be waking from sleep; retrying`);
      }
      if (!res.ok) {
        throw new Error(`space responded HTTP ${res.status}`);
      }
      const json = await res.json();
      if (!json.event_id) throw new Error('space did not return an event_id');
      return json.event_id;
    } catch (err) {
      lastErr = err;
      if (err.name === 'AbortError') throw err;
      if (attempt < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 1500 * attempt)); // backoff
      }
    }
  }
  throw lastErr;
}

/** Read the SSE result stream until `complete`, resolve with payload[0]. */
async function _awaitResult(spaceUrl, fn, eventId, signal, onProgress) {
  const base = spaceUrl.replace(/\/+$/, '');
  const res = await fetch(`${base}/call/${fn}/${eventId}`, { signal });
  if (!res.ok || !res.body) {
    throw new Error(`result stream failed (HTTP ${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let payload = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE events are separated by a blank line; parse complete blocks.
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const lines = block.split('\n');
      const eventLine = lines.find((l) => l.startsWith('event:'));
      const dataLine = lines.find((l) => l.startsWith('data:'));
      const eventName = eventLine ? eventLine.slice(6).trim() : 'message';
      if (eventName === 'complete') {
        const raw = dataLine ? dataLine.slice(5).trim() : 'null';
        payload = JSON.parse(raw)[0];
      } else if (eventName === 'error') {
        throw new Error('space reported an error event');
      } else if (eventName === 'progress' && onProgress) {
        try {
          const p = JSON.parse(dataLine.slice(5).trim())[0];
          onProgress(p);
        } catch (_e) { /* progress is best-effort */ }
      }
    }
  }
  if (payload === null) {
    throw new Error('stream ended without a complete event');
  }
  return payload;
}

/**
 * Call a Gradio endpoint on a Space.
 * @param {string} spaceUrl e.g. 'https://username-qianfan-ocr-import.hf.space'
 * @param {string} fn api_name, e.g. 'extract_fields'
 * @param {Array} data positional args for the endpoint
 * @param {{signal?: AbortSignal, onProgress?: Function, timeoutMs?: number}} [opts]
 * @returns {Promise<*>} first return value of the endpoint
 */
export async function callSpace(spaceUrl, fn, data, opts = {}) {
  const { signal, onProgress, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // Chain external abort into ours.
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    onProgress?.({ status: 'submitting' });
    const eventId = await _postJob(spaceUrl, fn, data, controller.signal);
    onProgress?.({ status: 'queued', eventId });
    return await _awaitResult(spaceUrl, fn, eventId, controller.signal, onProgress);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Convenience: extract certificate fields from an image/PDF blob.
 * @param {string} spaceUrl
 * @param {File|Blob} file image or PDF
 * @param {string} formId
 * @param {Object} fieldSpec schema-derived spec (see schemaPrompt.js)
 * @param {string} [formName]
 * @param {Object} [opts] callSpace opts + accessToken
 */
export async function extractFields(spaceUrl, file, formId, fieldSpec, formName = '', opts = {}) {
  const { accessToken, onProgress, ...callOpts } = opts;
  onProgress?.({ status: 'uploading' });
  const serverPath = await _uploadFile(spaceUrl, file, callOpts.signal);
  onProgress?.({ status: 'uploaded' });
  return callSpace(spaceUrl, 'extract_fields', [
    _fileData(serverPath),
    formId,
    JSON.stringify(fieldSpec),
    formName || '',
    accessToken || '',
  ], { ...callOpts, onProgress });
}

/** Convenience: raw OCR transcription (debug/fallback path). */
export async function extractText(spaceUrl, file, opts = {}) {
  const { accessToken, onProgress, ...callOpts } = opts;
  onProgress?.({ status: 'uploading' });
  const serverPath = await _uploadFile(spaceUrl, file, callOpts.signal);
  onProgress?.({ status: 'uploaded' });
  return callSpace(spaceUrl, 'extract_text', [_fileData(serverPath), accessToken || ''], { ...callOpts, onProgress });
}

/** Space health probe — resolves false instead of throwing when unreachable. */
export async function pingSpace(spaceUrl, opts = {}) {
  try {
    const result = await callSpace(spaceUrl, 'health', [], { timeoutMs: 15_000, ...opts });
    return result && result.ok !== false ? result : false;
  } catch (_e) {
    return false;
  }
}
